import { FC, useState } from "react";
import { useDetailBookingStore } from "../detail-booking.store";
import {
  Plus,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Eye,
  Edit,
} from "lucide-react";
import { capitalize } from "~/utils";
import { useGetTasks } from "~/services/task";
import { TaskStatus, Priority } from "~/utils/constants";
import { Spin, Tag } from "antd";
import { useTaskStore } from "~/components/admin/dispatch/task/list-tasks/task.store";
import { CreateTaskModal } from "~/components/admin/dispatch/task/create-task/create-task-modal";
import { useCompleteTask as useCompleteTaskStore } from "~/components/common/complete-task/complete-task.store";
import { CompleteTaskModal } from "~/components/common/complete-task/complete-task-modal";
import { TaskDetailDrawer } from "~/components/common/task-detail-drawer/task-detail-drawer";
import { Task } from "~/types/task";
import dayjs from "dayjs";

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle size={14} className="text-green-500" />;
    case TaskStatus.IN_PROGRESS:
      return <Clock size={14} className="text-blue-500" />;
    default:
      return <Clock size={14} className="text-gray-400" />;
  }
};

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "green";
    case TaskStatus.IN_PROGRESS:
      return "blue";
    default:
      return "default";
  }
};

const getPriorityColor = (priority: Priority) => {
  switch (priority) {
    case Priority.HIGH:
      return "red";
    case Priority.MEDIUM:
      return "orange";
    case Priority.LOW:
      return "green";
    default:
      return "default";
  }
};

// --- Task Card ---
const TaskCard: FC<{
  task: Task;
  onView: (taskId: string) => void;
  onEdit: (taskId: string) => void;
  onFinalize: (task: Task) => void;
}> = ({ task, onView, onEdit, onFinalize }) => {
  return (
    <div className="p-4 hover:bg-gray-50 border-b last:border-b-0">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          {getStatusIcon(task.taskStatus)}
          <span className="font-medium">{task.title}</span>
          <span className="text-xs text-gray-400">{task.taskRef}</span>
          {task.redFlag && (
            <Tag color="red" className="text-xs">
              Red Flag
            </Tag>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100 flex items-center gap-1"
            onClick={() => onView(task.id)}
          >
            <Eye size={12} />
            View
          </button>
          <button
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 flex items-center gap-1"
            onClick={() => onEdit(task.id)}
          >
            <Edit size={12} />
            Edit
          </button>
          {task.taskStatus !== TaskStatus.COMPLETED && (
            <button
              className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 flex items-center gap-1"
              onClick={() => onFinalize(task)}
            >
              <CheckCircle size={12} />
              Finalize
            </button>
          )}
          <Tag color={getPriorityColor(task.priority)}>
            {capitalize(task.priority)}
          </Tag>
          <Tag color={getStatusColor(task.taskStatus)}>
            {capitalize(task.taskStatus.replace("_", " "))}
          </Tag>
        </div>
      </div>

      {task.description && (
        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
          {task.description}
        </p>
      )}

      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
        {task.startDate && (
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {dayjs(task.startDate).format("MMM D, YYYY")}
            {task.startTime && (
              <span>
                {" "}
                {dayjs(`2025-01-01 ${task.startTime}`).format("h:mm A")}
              </span>
            )}
          </span>
        )}
        {task.dueDate && (
          <span className="flex items-center gap-1">
            Due: {dayjs(task.dueDate).format("MMM D, YYYY")}
          </span>
        )}
        {task.assignees && task.assignees.length > 0 && (
          <span className="flex items-center gap-1">
            <User size={12} />
            {task.assignees
              .map((a) =>
                a.user
                  ? `${a.user.firstName || ""} ${a.user.lastName || ""}`.trim()
                  : "Staff"
              )
              .join(", ")}
          </span>
        )}
        {task.duration && <span>{task.duration}h</span>}
      </div>
    </div>
  );
};

// --- Main Tasks Tab ---
export const Tasks: FC = () => {
  const { booking } = useDetailBookingStore();
  const { openTaskModal } = useTaskStore();
  const { openCompleteTask } = useCompleteTaskStore();
  const [viewingTaskId, setViewingTaskId] = useState<string | null>(null);

  const { data: tasksData, isLoading } = useGetTasks({
    jobTicketId: booking?.id,
    isArchived: false,
  });

  if (!booking) {
    return <div className="text-gray-500">No booking data available</div>;
  }

  const handleCreateTask = () => {
    openTaskModal(undefined, booking.id, booking.clientId);
  };

  const handleEditTask = (taskId: string) => {
    openTaskModal(taskId, booking.id, booking.clientId);
  };

  const tasks = tasksData?.items || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Tasks</h3>
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
          onClick={handleCreateTask}
        >
          <Plus size={16} />
          <span>New Task</span>
        </button>
      </div>

      <Spin spinning={isLoading}>
        <div className="border rounded-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h4 className="font-medium">
              Internal Tasks
              <span className="text-sm font-normal text-gray-500 ml-2">
                Linked to this booking
              </span>
            </h4>
          </div>

          {tasks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No tasks yet for this booking.</p>
              <p className="text-sm mt-1">
                Create a task to track internal work, assign staff, and set
                deadlines.
              </p>
            </div>
          ) : (
            <div>
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onView={setViewingTaskId}
                  onEdit={handleEditTask}
                  onFinalize={openCompleteTask}
                />
              ))}
            </div>
          )}
        </div>
      </Spin>

      {/* View sidebar */}
      <TaskDetailDrawer
        taskId={viewingTaskId}
        onClose={() => setViewingTaskId(null)}
        onEdit={handleEditTask}
        onFinalize={openCompleteTask}
      />

      <CreateTaskModal />
      <CompleteTaskModal />
    </div>
  );
};
